import { useEffect, useState, useSyncExternalStore } from "react";
import { useOutlineImport } from "@/hooks/useOutlineImport";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { UpgradeCard } from "@/components/UpgradeCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ProsePane from "./ProsePane";
import PreviewPane from "./PreviewPane";
import type {
  EditableClaim,
  ExtractedClaim,
  PreviewResponse,
  CommitRequest,
} from "@/lib/outline-import-types";

const MAX_CHARS = 30_000;
const MIN_CHARS = 20;

function subscribeResize(cb: () => void): () => void {
  const mq = window.matchMedia("(min-width: 1024px)");
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

function getIsDesktop(): boolean {
  return window.matchMedia("(min-width: 1024px)").matches;
}

interface Props {
  conceptId: string;
}

export default function OutlineImportPage({ conceptId }: Props) {
  const isDesktop = useSyncExternalStore(
    subscribeResize,
    getIsDesktop,
    () => true,
  );
  const { state, preview, error, runPreview, runCommit, resetToPaste } =
    useOutlineImport(conceptId);
  const { data: currentUser } = useCurrentUser();
  const paid = isPaidPlan(currentUser?.plan);
  const [prose, setProse] = useState("");

  if (currentUser && !paid) {
    return (
      <div className="fixed inset-0 z-50 bg-background text-foreground flex flex-col">
        <div className="px-4 py-3 border-b font-medium flex items-center justify-between">
          <span>Import outline</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            aria-label="close outline import"
          >
            ×
          </Button>
        </div>
        <div className="flex-1 min-h-0">
          <UpgradeCard
            feature="Outline Import"
            description="Turn drafted prose into structured claims and evidence in one pass."
          />
        </div>
      </div>
    );
  }

  if (!isDesktop) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-center">
        <div>
          <h2 className="text-lg font-semibold mb-2">
            Outline import needs a larger screen
          </h2>
          <p className="text-sm text-muted-foreground">
            Please switch to a desktop browser.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background text-foreground flex flex-col">
      <div className="px-4 py-3 border-b font-medium flex items-center justify-between">
        <span>Import outline</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.history.back()}
          aria-label="close outline import"
        >
          ×
        </Button>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {state === "paste" && (
          <div className="p-8 max-w-3xl mx-auto">
            {error && (
              <div className="mb-4 p-3 border border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-200 rounded text-sm">
                Preview failed: {error}
              </div>
            )}
            <div className="mb-2 text-sm text-muted-foreground bg-muted/40 border rounded p-3">
              Imports append to your existing claims. Re-running is safe — claims
              whose text already exists are skipped, not duplicated. Citations are
              matched to papers in your corpus; unmatched ones can be found on
              Semantic Scholar and added during review.
            </div>
            <Textarea
              value={prose}
              onChange={(e) => setProse(e.target.value.slice(0, MAX_CHARS))}
              placeholder="Paste 1–3 pages of prose with author-year citations like [Smith 2024] or [CITATION] placeholders."
              className="min-h-[400px] font-mono text-sm"
            />
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {prose.length} / {MAX_CHARS}
              </span>
              <span>Minimum {MIN_CHARS} characters.</span>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => window.history.back()}>
                Cancel
              </Button>
              <Button
                onClick={() => runPreview(prose)}
                disabled={prose.trim().length < MIN_CHARS}
              >
                Preview
              </Button>
            </div>
          </div>
        )}

        {state === "parsing" && <ParsingView />}

        {state === "preview" && preview && (
          <PreviewView
            conceptId={conceptId}
            preview={preview}
            onRepaste={resetToPaste}
            onCommit={(body) => {
              void runCommit(body);
            }}
          />
        )}

        {state === "committing" && <ParsingView label="Committing…" />}
      </div>
    </div>
  );
}

function ParsingView({ label }: { label?: string }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex h-full items-center justify-center flex-col gap-3">
      <div className="animate-spin border-4 border-gray-300 border-t-blue-600 rounded-full w-10 h-10" />
      <div className="text-sm text-muted-foreground">
        {label ?? "Extracting claims and matching citations…"}
      </div>
      {elapsed > 15 && (
        <div className="text-xs text-muted-foreground">
          This can take up to 30s for longer outlines.
        </div>
      )}
    </div>
  );
}

function toEditable(claims: ExtractedClaim[]): EditableClaim[] {
  return claims.map((c) => ({
    ...c,
    editedText: c.text,
    dropped: false,
    evidence: c.evidence.map((e) => ({
      ...e,
      skipped: false,
      selectedPaperId: e.match?.paper_id ?? null,
      selectedPaperTitle: e.match?.paper_title ?? null,
      selectedPaperAuthors: e.match?.paper_authors ?? null,
    })),
  }));
}

function PreviewView({
  conceptId,
  preview,
  onRepaste,
  onCommit,
}: {
  conceptId: string;
  preview: PreviewResponse;
  onRepaste: () => void;
  onCommit: (body: CommitRequest) => void;
}) {
  const [editable, setEditable] = useState<EditableClaim[]>(() =>
    toEditable(preview.claims),
  );
  useEffect(() => {
    setEditable(toEditable(preview.claims));
  }, [preview]);

  function handleCommit() {
    const claims = editable
      .filter((c) => !c.dropped)
      .map((c) => ({
        text: c.editedText,
        evidence: c.evidence
          .filter((e) => !e.skipped && e.selectedPaperId)
          .map((e) => ({
            paper_id: e.selectedPaperId!,
            excerpt_text: e.excerpt_text,
          })),
      }))
      .filter((c) => c.evidence.length > 0);
    onCommit({ claims });
  }

  function scrollToClaim(tmp_id: string) {
    const el = document.getElementById(tmp_id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="grid grid-cols-2 h-full">
      <div className="border-r overflow-hidden">
        <ProsePane
          proseText={preview.prose_text}
          claims={editable}
          onClickHighlight={scrollToClaim}
        />
      </div>
      <PreviewPane
        conceptId={conceptId}
        claims={editable}
        onClaimsChange={setEditable}
        onCommit={handleCommit}
        onRepaste={onRepaste}
      />
    </div>
  );
}
